// apps/vrw-cp - ミッション一覧ページ
import React from "react";
import { GetServerSideProps } from "next";
import { ParsedUrlQuery } from "querystring";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { TimeRange, CsvDownloadButton, MissionResultMap } from "@vrw-guppy/react-vrw";

import MissionList from "@/components/MissionList";
import MainBox from "@/components/common/MainBox";
import OgpHead from "@/components/OgpHead";
import SearchForm from "@/components/SearchForm";
import MissionStats from "@/components/MissionStats";
import {
  MissionSearchRequest,
  MissionReadResponse,
} from "@/lib/models/mission";
import { listItemNoCache } from "@/lib/apiClient";

type ListItemProps = {
  items: MissionReadResponse[];
  query: MissionSearchRequest;
};

/**
 * クエリパラメータからミッション検索クエリを生成する
 */
const buildMissionQuery = (query: ParsedUrlQuery): MissionSearchRequest => {
  const result: MissionSearchRequest = {};
  Object.entries(query).forEach(([key, value]) => {
    if (typeof value === "string") {
      // 配列ではなく文字列のみを使用
      result[key] = value;
    }
  });
  if (!result.datetime) {
    // datetime が指定されていない場合はデフォルトの範囲を設定
    result.datetime = TimeRange.fromMode(new Date(), "WEEK_TEIKI")
      .toString("datetime")
      .replace("datetime=", "");
  }
  return result;
};

/**
 * ミッション一覧データをSSRで取得し、キャッシュを完全に無効化する
 */
export const getServerSideProps: GetServerSideProps<ListItemProps> = async (
  context,
) => {
  // キャッシュを完全に無効化
  context.res.setHeader(
    "Cache-Control",
    "no-store, no-cache, must-revalidate, proxy-revalidate, max-age=0, s-maxage=0"
  );
  context.res.setHeader("Pragma", "no-cache");
  context.res.setHeader("Expires", "0");
  
  const query = buildMissionQuery(context.query);
  const items = await listItemNoCache({ type: "mission", params: query });

  return {
    props: {
      items,
      query,
    },
  };
};

const App = ({ items, query }: ListItemProps) => {
  
  return (
    <>
      <OgpHead title="ミッションを検索する"></OgpHead>
      <MainBox
        sx={{
          border: "1px solid #eaeaea",
          borderRadius: "4px",
          backgroundColor: "background.paper",
        }}
      >
        <SearchForm
          type="mission"
          query={query}
          seathKeys={[
            {
              label: "日時",
              value: "datetime",
              type: "datetime",
              enabled: false,
            },
            {
              label: "ミッション名",
              value: "name",
              type: "string",
              enabled: false,
            },
            {
              label: "ロケット",
              value: "rocket",
              type: "string",
              enabled: false,
            },
            {
              label: "発射場",
              value: "site",
              type: "string",
              enabled: false,
            },
            {
              label: "打ち上げ事業者",
              value: "provider",
              type: "string",
              enabled: false,
            },
            {
              label: "概要",
              value: "overview",
              type: "string",
              enabled: false,
            },
          ]}
        />
        <Typography variant="caption">
          注意: 時刻は国際標準時で検索してください。
        </Typography>
        <MissionStats items={items} />
      </MainBox>
      <MainBox>
        <MissionList items={items || []} />
        <Box sx={{ textAlign: 'right', mt: 1 }}>
          <CsvDownloadButton
            variant="outlined"
            headers={['rocket', 'name', 'datetime', 'datetime_format', 'result', 'site', 'overview']}
            rows={items.map((i) => [
              i.rocket || '',
              i.name,
              i.datetime,
              i.datetime_format || '',
              MissionResultMap[i.status]?.content || i.status,
              i.site || '',
              i.overview || '',
            ])}
            filename="mission_search.csv"
          >
            CSVをダウンロード
          </CsvDownloadButton>
        </Box>
      </MainBox>
    </>
  );
};

export default App;
